import React, { useState } from 'react';
import { AnimationType } from '../types/animations';

interface Suggestion {
  selector: string; // CSS selector to find element(s) in the SVG
  animation: AnimationType;
  params?: Record<string, unknown>;
  reason?: string;
}

interface AIAnimatorProps {
  svgContainerRef: React.RefObject<HTMLDivElement | null>;
  setSelectedAnimation: (a: AnimationType) => void;
  setSelectedElement: (e: SVGElement | null) => void;
}

const DEFAULT_PROMPT = `Please analyze the following SVG markup and return a JSON object (only JSON) with an array field named \"suggestions\". Each suggestion must contain: \n- selector: a CSS selector (id, class, or tag) that targets one or more elements in the SVG\n- animation: one of the known animation names (choose a single animation per suggestion)\n- params: optional object with parameters like speed or intensity\n- reason: short human-readable justification for the suggestion\n\nExample: {\"suggestions\": [{\"selector\": \"circle\", \"animation\": \"bouncing\", \"params\": {\"speed\": 2}, \"reason\": \"Circles look good with a bounce\"}]}\n\nNow analyze the SVG below and return suggestions.
\nSVG:\n\n`;

const AIAnimator: React.FC<AIAnimatorProps> = ({ svgContainerRef, setSelectedAnimation, setSelectedElement }) => {
  const [apiKey, setApiKey] = useState<string>(localStorage.getItem('openai_api_key') || '');
  const [prompt, setPrompt] = useState<string>(DEFAULT_PROMPT);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [error, setError] = useState<string | null>(null);

  const saveKey = () => {
    localStorage.setItem('openai_api_key', apiKey);
    setError(null);
  };

  const analyzeSvg = async () => {
    setError(null);
    setSuggestions([]);

    if (!apiKey) {
      setError('Please enter your OpenAI API key.');
      return;
    }

    const svg = svgContainerRef.current?.querySelector('svg')?.outerHTML;
    if (!svg) {
      setError('No SVG found in the canvas.');
      return;
    }

    // Build the user prompt by concatenating the user's prompt with the svg wrapped in xml fences
    const userPrompt = `${prompt}\n\n---SVG_START---\n${svg}\n---SVG_END---`;

    setLoading(true);
    try {
      const res = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: 'You are an assistant that only outputs valid JSON according to the schema requested.' },
            { role: 'user', content: userPrompt },
          ],
          temperature: 0.6,
          max_tokens: 800,
        }),
      });

      if (!res.ok) {
        const text = await res.text();
        setError(`OpenAI API error: ${res.status} ${res.statusText} - ${text}`);
        setLoading(false);
        return;
      }

      const data = await res.json();
      const reply = data?.choices?.[0]?.message?.content;

      if (!reply) {
        setError('No reply content from OpenAI');
        setLoading(false);
        return;
      }

      // Try to parse JSON from the reply
      let parsed: any = null;
      try {
        // Some models will include text around JSON; extract first JSON object substring
        const firstBrace = reply.indexOf('{');
        const lastBrace = reply.lastIndexOf('}');
        if (firstBrace >= 0 && lastBrace >= 0) {
          const jsonText = reply.slice(firstBrace, lastBrace + 1);
          parsed = JSON.parse(jsonText);
        } else {
          parsed = JSON.parse(reply);
        }
      } catch (e) {
        setError('Failed to parse JSON from model response. Response was:\n' + reply);
        setLoading(false);
        return;
      }

      const s: Suggestion[] = Array.isArray(parsed.suggestions) ? parsed.suggestions : [];
      setSuggestions(s);
    } catch (e: any) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  const applySuggestion = (s: Suggestion) => {
    const svg = svgContainerRef.current?.querySelector('svg');
    if (!svg) {
      setError('No SVG present');
      return;
    }

    const elem = svg.querySelector(s.selector) as SVGElement | null;
    if (!elem) {
      setError(`Selector did not match any element: ${s.selector}`);
      return;
    }

    // Set selection and animation in the host app
    setSelectedElement(elem);
    setSelectedAnimation(s.animation);
  };

  return (
    <div className="ai-animator card mb-3 p-3">
      <h4>AI Animator</h4>
      <p className="small text-muted">Enter your OpenAI API key (stored locally) and ask the model to suggest animations for the current SVG canvas.</p>

      <div className="mb-2">
        <label className="form-label">OpenAI API Key</label>
        <input type="password" className="form-control" value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
        <div className="d-flex gap-2 mt-2">
          <button className="btn btn-sm btn-primary" onClick={saveKey}>Save key</button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => { setApiKey(''); localStorage.removeItem('openai_api_key'); }}>Remove</button>
        </div>
      </div>

      <div className="mb-2">
        <label className="form-label">Prompt (you can edit)</label>
        <textarea rows={4} className="form-control" value={prompt} onChange={(e) => setPrompt(e.target.value)} />
      </div>

      <div className="mb-2 d-flex gap-2">
        <button className="btn btn-success" onClick={analyzeSvg} disabled={loading}>{loading ? 'Analyzing…' : 'Analyze SVG'}</button>
        <button className="btn btn-outline-secondary" onClick={() => { setSuggestions([]); setError(null); }}>Clear</button>
      </div>

      {error && <div className="alert alert-danger mt-2">{error}</div>}

      {suggestions.length > 0 && (
        <div className="mt-3">
          <h5>Suggestions</h5>
          <ul className="list-group">
            {suggestions.map((s, i) => (
              <li key={i} className="list-group-item d-flex justify-content-between align-items-start">
                <div>
                  <div><strong>Selector:</strong> <code>{s.selector}</code></div>
                  <div><strong>Animation:</strong> {String(s.animation)}</div>
                  {s.reason && <div className="text-muted small">{s.reason}</div>}
                </div>
                <div className="btn-group">
                  <button className="btn btn-sm btn-primary" onClick={() => applySuggestion(s)}>Apply</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AIAnimator;
